import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_URL, NAV_LINKS, scrollToSection } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { LanguageSwitcher } from "./LanguageSwitcher";

const KEY_MAP = {
  Collections: "nav.collections", Automotive: "nav.automotive", Aviation: "nav.aviation",
  Yachting: "nav.yachting", "Real Estate": "nav.realEstate", Sold: "nav.sold",
  Bespoke: "nav.bespoke", Fees: "nav.fees", About: "nav.about", Contact: "nav.contact",
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useI18n();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLink = (link) => {
    setOpen(false);
    if (link.path) {
      navigate(link.path);
      window.scrollTo(0, 0);
      return;
    }
    if (location.pathname === "/") scrollToSection(link.target);
    else navigate("/", { state: { scrollTo: link.target } });
  };

  const goHome = () => {
    if (location.pathname === "/") scrollToSection("home");
    else navigate("/");
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass py-3" : "bg-transparent py-5"}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={goHome} className="flex items-center gap-3 group">
          <img src={LOGO_URL} alt="Royce International" className="h-12 w-auto object-contain" />
        </button>

        <nav className="hidden lg:flex items-center gap-4">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => handleLink(l)}
              className="font-sans text-[12px] tracking-wide-lux uppercase text-white/70 hover:text-gold transition-colors duration-300"
            >
              {t(KEY_MAP[l.label] || l.label)}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <LanguageSwitcher />
          <button
            data-testid="nav-private-access"
            onClick={() => navigate(user ? "/dashboard" : "/private-access")}
            className="group flex items-center gap-2 border border-gold/50 text-gold px-5 py-2.5 rounded-md text-[11px] tracking-wide-lux uppercase hover:bg-gold hover:text-navy transition-all duration-400"
          >
            {user ? t("nav.myAccount") : t("nav.privateAccess")}
            <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-4">
          <LanguageSwitcher compact />
          <button data-testid="nav-mobile-toggle" className="text-white" onClick={() => setOpen((o) => !o)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="lg:hidden glass mt-3 overflow-hidden">
            <div className="flex flex-col px-6 py-6 gap-4">
              {NAV_LINKS.map((l) => (
                <button key={l.label} onClick={() => handleLink(l)} className="text-left font-sans text-sm tracking-wide-lux uppercase text-white/80 hover:text-gold">
                  {t(KEY_MAP[l.label] || l.label)}
                </button>
              ))}
              <button onClick={() => { setOpen(false); navigate(user ? "/dashboard" : "/private-access"); }} className="mt-2 border border-gold/50 text-gold px-5 py-3 rounded-md text-xs tracking-wide-lux uppercase">
                {user ? t("nav.myAccount") : t("nav.privateAccess")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
